import { Button, Card, FluentProvider, makeStyles, Spinner, Text } from "@fluentui/react-components";
import { PlayRegular } from "@fluentui/react-icons";
import { useState } from "react";
import { useIsGlobalAdmin } from "../apis/GraphApi";
import { useGetLastAnalysisResult, useGetUserInfo, useHasLicenseOptimizerPermissions, useRunAnalyticsByUserMutation } from "../apis/TenantOptimizerApi";
import { PageLayout } from "../components";
import { HomeButton } from "../components/buttons";
import { ServiceUnavailableDialog, AccessDeniedAlert, TenantNotRegisteredAlert } from "../components/dialogs";
import config from "../config";
import { MessageInfo } from "../MessageContext";
import { dateTimeFormatOptions } from "../utils/DateTimeFormatOptions";

const useStyles = makeStyles({
  page: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "100%"
  },
  messageBar: {
    width: "740px",
    maxWidth: "100%"
  },
  homeButton: {
    marginLeft: 'auto'
  },
  card: {
    width: '700px',
    maxWidth: "100%",
    gap: '30px'
  },
  buttonsContaners: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center'
  },
  descriptionText: {
    textAlign: "justify"
  },
  explanationText: {
    color: '#c3c3c3',
    width: '384px',
    marginBottom: '5px',
    display: 'flex',
    flexDirection: 'column-reverse'
  },
  runButton: {
    width: '384px',
    height: '55px',
    fontSize: "22px"
  },
  alphaText: {
    textAlign: 'justify',
    marginTop: '16px',
    color: '#979797',
    fontSize: '15px',
    padding: '10px',
    lineHeight: '1.5'
  }
});

const TenantOptimizerPage = () => {
  const styles = useStyles();
  const { data: isTenantOptimizerRegisteredData, isError: isPermissionCheckError } = useHasLicenseOptimizerPermissions();
  const { data: isGlobalAdmin, isLoading: isGlobalAdminLoading } = useIsGlobalAdmin();
  const { data: user, isFetching: isUserFetching } = useGetUserInfo();
  const { data: analysisStatus, isLoading: isAnalysisLoading } = useGetLastAnalysisResult(isGlobalAdmin && isTenantOptimizerRegisteredData?.HasPermissions);
  const [isTenantNotRegisteredAlertOpen, setIsTenantNotRegisteredAlertOpen] = useState(false);
  const [message, setMessage] = useState<MessageInfo>(null);

  const mutation = useRunAnalyticsByUserMutation(() => {
    setMessage({
      intent: "success",
      title: "Analysis successfully initiated",
      description: "Please wait while we process your tenant data"
    });
  },
  () => {
    setMessage({
      intent: "error",
      title: "Unable to initiate tenant analysis",
      description: <>Please try again in a few minutes or <a href="mailto:dev_support@o365hq.com">contact support</a> if the issue persists.</>
    });
  });

  const onRunAnalysisClick = async () => {
    if (isTenantOptimizerRegisteredData?.HasPermissions) {
      mutation.mutate(user.tenantId);
    } else {
      setIsTenantNotRegisteredAlertOpen(true);
    }
  }

  const getAnalysisStatusText = () => {
    if (!analysisStatus) return "";

    let text = (analysisStatus.Status === 'Pending' || analysisStatus.Status === 'InProgress') ?
      "Tenant analysis is in progress. Started" : "The last tenant analysis was initiated";
    if (analysisStatus.InitiatorName) {
      text += ` by ${analysisStatus.InitiatorName}`;
      if (analysisStatus.InitiatorEmail) {
        text += ` (${analysisStatus.InitiatorEmail})`;
      }
    }
    const timestamp = new Date(analysisStatus?.InitiatedAt).toLocaleString('en-US', dateTimeFormatOptions);
    text += ` on ${timestamp}`;
    return text;
  }

  if (isGlobalAdminLoading || isUserFetching || !isTenantOptimizerRegisteredData || isAnalysisLoading) {
    return (
      <div className={styles.page}>
        <Spinner/>
        <ServiceUnavailableDialog
          title="Tenant Optimizer is currently unavailable"
          message={"We're performing scheduled maintenance to improve our services. Tenant Optimizer will be temporarily unavailable. Our team is working to bring the service back online as quickly as possible. Thank you for your patience"}
          open={isPermissionCheckError}
        />
      </div>
    );
  }

  return (
    <FluentProvider theme={{
      colorBrandBackground: '#0099DA',
      colorBrandBackgroundHover: '#0077A0',
      colorBrandBackgroundPressed: '#006688'
    }}>
      <PageLayout message={message} onDismiss={() => setMessage(null)}>
        <AccessDeniedAlert
          reason={"Only Global Admins can request an analytical report."}
          open={!isGlobalAdminLoading && !isGlobalAdmin}
        />
        <TenantNotRegisteredAlert
          isOpen={isTenantNotRegisteredAlertOpen}
          close={() => {
            setIsTenantNotRegisteredAlertOpen(false);
          }}
          onGrantSuccess={() => {
            setMessage({
              intent: "success",
              title: "Tenant registered successfully"
            });
          }}
          clientId={config.tenantOptimizerClientId}
          redirectUri={config.tenantOptimizerBackendUrl + "/GrantingCallback"}
        />
        {isGlobalAdmin && <Card className={styles.card}>
          <HomeButton 
            className={styles.homeButton}
            appearance="subtle"
            size="large"
          />
          <Text className={styles.descriptionText} size={400}>
            To analyze your tenant and prepare a report, click 'Run Tenant Analysis' button.
            After the analysis is completed, the report will be generated and sent to your email{" "}
            <strong>{user.preferredUserName}</strong>.
          </Text>
          <div className={styles.buttonsContaners}>
            <Text className={styles.explanationText}>
              {getAnalysisStatusText()}
            </Text>
            <Button 
              className={styles.runButton}
              disabled={mutation.isLoading || (analysisStatus && (analysisStatus.Status === 'Pending' || analysisStatus.Status === 'InProgress'))}
              appearance="primary"
              size="large"
              icon={<PlayRegular/>}
              onClick={onRunAnalysisClick}
            >
              Run Tenant Analysis
            </Button>
          </div>
          <Text className={styles.alphaText}>
            This is the alpha version. We're actively testing design, enhancing functionality,
            and system performance. Our goal is high quality and accurate data,
            but reports and recommendations may contain errors. Please report any issues!
          </Text>
        </Card>}
      </PageLayout>
    </FluentProvider>
  );
};

export default TenantOptimizerPage;

